import React, { useState, useEffect } from 'react';
import axios from 'axios';

const DisscussAsk = () => {
  const [questions, setQuestions] = useState([]);
  const [approvedQuestions, setApprovedQuestions] = useState([]);
  const [studentDetails, setStudentDetails] = useState({});
  const [approverDetails, setApproverDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'approved', 'pending'
  const [userId, setUserId] = useState(null);

  // Fetch data on component mount
  useEffect(() => {
    // Get user ID from localStorage
    const storedUserId = localStorage.getItem('user_id');
    if (storedUserId) {
      setUserId(parseInt(storedUserId, 10));
    }
    
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      
      const [questionsRes, approvedRes] = await Promise.all([
        axios.get('https://apissc.sainikschoolcadet.com/api/StudentQuestions/all'),
        axios.get('https://apissc.sainikschoolcadet.com/api/ApprovedQuestions/')
      ]);
      
      setQuestions(questionsRes.data);
      setApprovedQuestions(approvedRes.data);
      
      // Fetch student details for each unique student ID
      const uniqueStudentIds = [...new Set(questionsRes.data.map(q => q.S_id))];
      const studentDetailsPromises = uniqueStudentIds.map(studentId => 
        axios.get(`https://apissc.sainikschoolcadet.com/api/Students/getbyid/${studentId}`)
          .then(res => ({ [studentId]: res.data }))
          .catch(() => ({ [studentId]: null })) // Handle if student not found
      );
      
      // Fetch approver details for each unique approver ID
      const uniqueApproverIds = [...new Set(approvedRes.data.map(aq => aq.approved_by))];
      const approverDetailsPromises = uniqueApproverIds.map(approverId => 
        axios.get(`https://apissc.sainikschoolcadet.com/api/users/getbyid/${approverId}`)
          .then(res => ({ [approverId]: res.data }))
          .catch(() => ({ [approverId]: null })) // Handle if approver not found
      );
      
      const [studentsData, approversData] = await Promise.all([
        Promise.all(studentDetailsPromises),
        Promise.all(approverDetailsPromises)
      ]);
      
      const studentsMap = studentsData.reduce((acc, curr) => ({ ...acc, ...curr }), {});
      const approversMap = approversData.reduce((acc, curr) => ({ ...acc, ...curr }), {});
      
      setStudentDetails(studentsMap);
      setApproverDetails(approversMap);
      
      setLoading(false);
    } catch (err) {
      setError('Failed to load questions. Please refresh the page.');
      setLoading(false);
      console.error('Error fetching data:', err);
    }
  };

  // Function to check if a question is approved
  const isQuestionApproved = (questionId) => {
    return approvedQuestions.some(aq => aq.q_id === questionId);
  };

  // Function to get approval record for a question
  const getApprovalRecord = (questionId) => {
    return approvedQuestions.find(aq => aq.q_id === questionId) || null;
  };

  // Function to get approver name for a question
  const getApproverName = (questionId) => {
    const approval = getApprovalRecord(questionId);
    if (!approval) return null;
    
    const approver = approverDetails[approval.approved_by];
    return approver ? approver.f_name : `ID: ${approval.approved_by}`;
  };

  // Function to format approval date
  const formatApprovalDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Function to approve a question
  const approveQuestion = async (questionId) => {
    // Check if user ID is available
    if (!userId) {
      alert('User ID not found. Please log in again.');
      return;
    }
    
    try {
      await axios.post('https://apissc.sainikschoolcadet.com/api/ApprovedQuestions/', {
        q_id: questionId,
        approved_by: userId
      });
      
      // Refresh data after approval
      fetchAllData();
      
    } catch (err) {
      alert('Failed to approve question. Please try again.');
      console.error('Error approving question:', err);
    }
  };

  // Function to unapprove (delete approval of) a question
  const unapproveQuestion = async (questionId) => {
    try {
      const approval = getApprovalRecord(questionId);
      
      if (!approval) {
        alert('Could not find approval record for this question.');
        return;
      }
      
      await axios.delete(`https://apissc.sainikschoolcadet.com/api/ApprovedQuestions/${approval.id}`);
      
      // Refresh data after unapproval
      fetchAllData();
      
    } catch (err) {
      alert('Failed to unapprove question. Please try again.');
      console.error('Error unapproving question:', err);
    }
  };

  // Function to permanently delete a question (and its approval, if any)
  const deleteQuestion = async (questionId) => {
    if (!window.confirm(`Delete question #${questionId}? This cannot be undone.`)) {
      return;
    }

    try {
      const approval = getApprovalRecord(questionId);

      // Remove the approval first so the app never lists a missing question
      if (approval) {
        await axios.delete(`https://apissc.sainikschoolcadet.com/api/ApprovedQuestions/${approval.id}`);
      }

      await axios.delete(`https://apissc.sainikschoolcadet.com/api/StudentQuestions/${questionId}`);

      // Refresh data after deletion
      fetchAllData();

    } catch (err) {
      alert('Failed to delete question. Please try again.');
      console.error('Error deleting question:', err);
      fetchAllData();
    }
  };

  // Filter questions based on active tab
  const filteredQuestions = questions.filter(question => {
    const approved = isQuestionApproved(question.q_id);
    
    if (activeTab === 'all') return true;
    if (activeTab === 'approved') return approved;
    if (activeTab === 'pending') return !approved;
    
    return true;
  });

  // Get the correct answer number (1-4) from option_X
  const getCorrectAnswerNumber = (correctAnswer) => {
    return correctAnswer.replace('option_', '');
  };

  if (loading) return <div className="flex justify-center items-center h-screen text-gray-500">Loading questions...</div>;
  if (error) return <div className="flex justify-center items-center h-screen text-red-500">{error}</div>;

  // Count pending questions
  const pendingCount = questions.filter(q => !isQuestionApproved(q.q_id)).length;

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="px-6 pt-6">
        <h1 className="text-2xl font-semibold text-gray-800">Question Approval</h1>
        <p className="text-gray-600 mt-1">Pending questions: {pendingCount}</p>
      </div>
      
      <div className="flex border-b border-gray-200 mt-6 px-6">
        <div 
          className={`px-4 py-2 cursor-pointer font-medium ${activeTab === 'all' ? 'text-blue-600 border-b-2 border-blue-500' : 'text-gray-600 hover:text-gray-800'}`}
          onClick={() => setActiveTab('all')}
        >
          All
        </div>
        <div 
          className={`px-4 py-2 cursor-pointer font-medium ${activeTab === 'approved' ? 'text-blue-600 border-b-2 border-blue-500' : 'text-gray-600 hover:text-gray-800'}`}
          onClick={() => setActiveTab('approved')}
        >
          Approved
        </div>
        <div 
          className={`px-4 py-2 cursor-pointer font-medium ${activeTab === 'pending' ? 'text-blue-600 border-b-2 border-blue-500' : 'text-gray-600 hover:text-gray-800'}`}
          onClick={() => setActiveTab('pending')}
        >
          Pending
        </div>
      </div>
      
      <div className="px-6 py-6">
        {!userId && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  User ID not found. Some features may be limited. Please try logging in again.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {filteredQuestions.length === 0 ? (
          <div className="bg-white rounded-md shadow-sm p-8 text-center text-gray-500">
            No questions to display
          </div>
        ) : (
          <div className="space-y-4">
            {filteredQuestions.map(question => {
              const approved = isQuestionApproved(question.q_id);
              const student = studentDetails[question.S_id] || {};
              const approvalRecord = getApprovalRecord(question.q_id);
              const approverName = getApproverName(question.q_id);
              
              return (
                <div key={question.q_id} className="bg-white rounded-md border border-gray-200 overflow-hidden shadow-sm">
                  <div className="flex justify-between items-center bg-gray-50 px-4 py-3 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                      <span className="font-bold text-gray-700">#{question.q_id}</span>
                      <span className="text-blue-500 font-medium">{question.subject}</span>
                      <span className="text-gray-500 text-sm">Class {question.class}</span>
                    </div>
                    {approved && (
                      <div className="flex items-center">
                        <span className="bg-blue-50 text-blue-600 text-xs font-medium px-2.5 py-0.5 rounded mr-2">
                          Approved
                        </span>
                        <span className="text-gray-500 text-xs">
                          by <span className="font-medium text-gray-700">{approverName}</span>
                          {approvalRecord && (
                            <span className="ml-1">on {formatApprovalDate(approvalRecord.approved_at)}</span>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Question in quiz format */}
                  <div className="p-6">
                    <h2 className="text-lg font-medium text-gray-800 mb-6">{question.question_text}</h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <div className="bg-gray-50 p-3 rounded-md hover:bg-gray-100 transition-colors">
                        <span className="inline-block w-6 h-6 mr-2 bg-gray-200 text-center rounded-full">1</span>
                        {question.option_1}
                      </div>
                      <div className="bg-gray-50 p-3 rounded-md hover:bg-gray-100 transition-colors">
                        <span className="inline-block w-6 h-6 mr-2 bg-gray-200 text-center rounded-full">2</span>
                        {question.option_2}
                      </div>
                      <div className="bg-gray-50 p-3 rounded-md hover:bg-gray-100 transition-colors">
                        <span className="inline-block w-6 h-6 mr-2 bg-gray-200 text-center rounded-full">3</span>
                        {question.option_3}
                      </div>
                      <div className="bg-gray-50 p-3 rounded-md hover:bg-gray-100 transition-colors">
                        <span className="inline-block w-6 h-6 mr-2 bg-gray-200 text-center rounded-full">4</span>
                        {question.option_4}
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center border-t border-gray-100 pt-4">
                      <div className="flex items-center">
                        <span className="text-blue-600 font-medium mr-2">Correct:</span>
                        <span className="inline-block w-6 h-6 mr-2 bg-blue-100 text-blue-800 text-center rounded-full font-medium">
                          {getCorrectAnswerNumber(question.correct_answer)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Student info and approval buttons */}
                  <div className="border-t border-gray-200 px-6 py-4">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center">
                        {student && student.f_name && (
                          <>
                            <div className="bg-blue-100 rounded-full w-10 h-10 flex items-center justify-center mr-3">
                              <span className="text-blue-800 font-medium text-lg">
                                {student.f_name.charAt(0)}
                              </span>
                            </div>
                            <div>
                              <div className="font-medium text-gray-800">{student.f_name}</div>
                              <div className="text-gray-500 text-sm">State: {student.state}</div>
                            </div>
                          </>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <button
                          className="border border-red-300 text-red-600 hover:bg-red-50 px-4 py-2 rounded-md text-sm font-medium transition-colors"
                          onClick={() => deleteQuestion(question.q_id)}
                        >
                          Delete
                        </button>
                        {approved ? (
                          <button 
                            className="bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded-md text-sm font-medium transition-colors"
                            onClick={() => unapproveQuestion(question.q_id)}
                          >
                            Unapprove
                          </button>
                        ) : (
                          <button 
                            className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-md text-sm font-medium transition-colors"
                            onClick={() => approveQuestion(question.q_id)}
                            disabled={!userId}
                          >
                            Approve
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DisscussAsk;